import { useState } from 'react'
import styled from 'styled-components'

import { Button } from '@syconium/little-miss-figgy/dist/components/Button'
import { LoadingSpinner } from '@syconium/little-miss-figgy/dist/components/LoadingSpinner/LoadingSpinner'

import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'
import {
  useEmbroidery,
  useEmbroideryDialog,
} from '../../../../containers/EmbroideryContainer/EmbroideryProvider'
import { useMakeEmbroideredCartItem } from '../../../../containers/EmbroideryContainer/useMakeEmbroideredCartItem'

/**
 * Cart embroidery upsell "Add To Bag" button.
 *
 * Unlike the PDP `EmbroideryAddToBag`, this does not depend on a
 * `VariantSelectionForm` (size/color are already fixed by the cart line). It
 * builds the embroidered items from the provider's `cartUpsellItem` at that
 * line's `quantity` and hands them to `onAddEmbroidery`,
 * which converts the existing cart line (see `addEmbroideryToExistingCartItem`).
 */
export const CartEmbroideryAddToBag: React.FC = () => {
  const makeEmbroideredCartItem = useMakeEmbroideredCartItem()
  const { confirmAndClose } = useEmbroideryDialog()
  const {
    cartUpsellItem,
    onAddEmbroidery,
    hasSubmittedEmbroiderySelections,
    embroideryLineItemProperties,
    embroiderySelectionsEnabledForBag,
  } = useEmbroidery()

  const {
    embroidery: {
      embroideryUpsell: { AddToBag: addToBagText },
    },
  } = useTranslation()

  // Tracks the in-flight conversion. We hold the modal open while `onAddEmbroidery`
  // runs (see below), so without this guard the user could re-click during the
  // network round-trip and trigger a duplicate conversion.
  const [isConverting, setIsConverting] = useState(false)

  const isEmbroideryReady =
    Boolean(embroideryLineItemProperties) &&
    Boolean(embroiderySelectionsEnabledForBag.embroideryItemVariant)

  const isDisabled =
    isConverting ||
    !cartUpsellItem ||
    !onAddEmbroidery ||
    !hasSubmittedEmbroiderySelections ||
    !isEmbroideryReady

  const onClick = async () => {
    if (isDisabled) return

    const { quantity, ...itemProps } = cartUpsellItem
    const embroideredItems = makeEmbroideredCartItem(
      {
        ...itemProps,
        options: {
          quantity,
          attributedPortal: undefined,
        },
      },
      cartUpsellItem.variantGroup.primaryCategory.id
    )

    setIsConverting(true)
    try {
      await onAddEmbroidery(embroideredItems)
      confirmAndClose()
    } finally {
      setIsConverting(false)
    }
  }

  return (
    <ATBButton
      aria-disabled={isDisabled}
      disabled={isDisabled}
      onClick={isDisabled ? undefined : onClick}
    >
      <ATBLabel $isConverting={isConverting}>{addToBagText}</ATBLabel>
      {isConverting ? <ATBSpinner size='20px' label={addToBagText} /> : null}
    </ATBButton>
  )
}

const ATBButton = styled(Button)`
  position: relative;
  padding: ${o => o.theme.spacing(4, 8)};
  margin: ${o => o.theme.spacing(2.5, 0, 0, 0)};
  line-height: ${o => o.theme.spacing(4.5)};
  width: 100%;

  &[disabled],
  &[aria-disabled='true'] {
    cursor: not-allowed;
    opacity: 0.6;
  }
`

const ATBLabel = styled.span<{ $isConverting: boolean }>`
  ${o => (o.$isConverting ? 'visibility: hidden;' : '')}
`

const ATBSpinner = styled(LoadingSpinner)`
  position: absolute;
  inset: 0;
  margin: auto;
`
